import type { SubAttachmentState } from '@/msgdeskRuntime/state'
import type { SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'

// Standard-surface-owned comments row. The sender line (author + address) is
// the shared skeleton control controls.emailFrom — this variant opts in and
// only styles its structural classes; it does not rebuild the address rule.

function initial(value: string) {
  return String(value || '?').trim().slice(0, 1).toUpperCase() || '?'
}

function attachmentLabel(attachment: SubAttachmentState) {
  return attachment.title || attachment.filename || attachment.storedName || 'Attachment'
}

function attachmentExtension(attachment: SubAttachmentState) {
  const label = attachmentLabel(attachment)
  const extension = label.includes('.') ? label.split('.').pop() : ''
  return String(extension || 'FILE').slice(0, 4).toUpperCase()
}

function attachmentSize(size?: number) {
  if (!size || size < 1) return ''
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function LegacyCommentsTemplate({ display, controls, slots }: SubTemplateProps) {
  const author = display.identity.author || display.email.fromDisplay || display.identity.address || display.title || 'Unknown sender'
  const hasChildren = !display.access.isContentLocked && (display.counts.answers > 0 || display.counts.submsges > 0)
  const hasSubject = Boolean(display.title && display.title !== `Sub ${display.elId}`)

  return (
    <>
      <article
        className={`md-legacy-answer-row group clear-both mx-2 mb-2 overflow-hidden rounded-md border bg-white font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Arial,sans-serif] text-[14px] text-[#17202a] transition-[border-color,box-shadow,background-color] hover:border-[#b8c5d1] hover:shadow-[0_2px_8px_rgba(23,32,42,0.08)] max-[640px]:mx-0 max-[640px]:rounded-none max-[640px]:border-x-0${display.isChecked ? ' md-legacy-answer-row--checked border-[#4d88bd] bg-[#f4f8fc]' : ' border-[#dce3e9]'}${display.state.isRead ? '' : ' md-legacy-answer-row--unread border-l-[3px] border-l-[#2d74a8]'}`}
        data-md-sub-template="comments"
      >
        <div className="md-legacy-answer-wrap grid grid-cols-[44px_minmax(0,1fr)] gap-3 px-4 py-3.5 max-[640px]:grid-cols-[36px_minmax(0,1fr)] max-[640px]:gap-2.5 max-[640px]:px-3">
          <div className="md-legacy-answer-left flex justify-center">
            <div className={`md-legacy-answer-avatar relative flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-full bg-[#315f7d] text-[14px] font-semibold text-white transition-[box-shadow,transform] max-[640px]:h-9 max-[640px]:w-9${display.permissions.canCheck ? ' cursor-pointer hover:scale-[1.03] hover:ring-2 hover:ring-[#9bb8cb] hover:ring-offset-2' : ''}${display.isChecked ? ' ring-2 ring-[#2d74a8] ring-offset-2' : ''}`}>
              <span aria-hidden="true">{initial(author)}</span>
              {display.media.avatarPath ? (
                <img
                  className="absolute inset-0 h-full w-full object-cover"
                  src={display.media.avatarPath}
                  alt=""
                  onError={(event) => { event.currentTarget.style.display = 'none' }}
                />
              ) : null}
              {display.permissions.canCheck ? (
                <span className="md-legacy-answer-check absolute inset-0 z-10 [&_.md-sub-icon]:absolute [&_.md-sub-icon]:inset-0 [&_.md-sub-icon]:m-0 [&_.md-sub-icon]:h-full [&_.md-sub-icon]:w-full [&_.md-sub-icon]:rounded-full [&_.md-sub-icon]:border-transparent [&_.md-sub-icon]:bg-transparent">
                  {controls.actions.check}
                </span>
              ) : null}
              {display.isChecked ? (
                <span className="pointer-events-none absolute bottom-0.5 right-0.5 z-20 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#2d74a8]" />
              ) : null}
            </div>
          </div>

          <div className="md-legacy-answer-main min-w-0">
            <header className="md-legacy-answer-header flex min-w-0 items-start justify-between gap-3 max-[520px]:block">
              <div className="min-w-0">
                <div className="md-legacy-answer-from flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 [&_.md-sub-email-from]:contents [&_.md-sub-email-from__author]:min-w-0 [&_.md-sub-email-from__author]:break-words [&_.md-sub-email-from__author]:text-[14px] [&_.md-sub-email-from__author]:font-semibold [&_.md-sub-email-from__author]:leading-5 [&_.md-sub-email-from__author]:text-[#17202a] [&_.md-sub-email-from__address]:min-w-0 [&_.md-sub-email-from__address]:break-all [&_.md-sub-email-from__address]:text-[12px] [&_.md-sub-email-from__address]:leading-5 [&_.md-sub-email-from__address]:text-[#64717d]">
                  {controls.emailFrom}
                  {!display.state.isRead ? (
                    <span className="h-1.5 w-1.5 flex-none rounded-full bg-[#2d74a8]" title="Unread" />
                  ) : null}
                </div>
                {hasSubject ? (
                  <div className="md-legacy-answer-subject mt-0.5 min-w-0 break-words leading-5 [&_.md-sub-title-link]:font-medium [&_.md-sub-title-link]:text-[#315f7d] [&_.md-sub-title-link]:underline-offset-2 hover:[&_.md-sub-title-link]:underline">
                    {controls.content.title}
                  </div>
                ) : null}
              </div>
              <div className="md-legacy-answer-meta flex-none whitespace-nowrap pt-0.5 text-[12px] leading-5 text-[#72808d] max-[520px]:mt-0.5 max-[520px]:pt-0">
                {controls.content.time || 'Unknown'}
              </div>
            </header>

            {/* The bridge replaces this shared text control with the protected
                preview leaf while keeping this ordinary sibling layout. */}
            <div className="md-legacy-answer-snippet mt-3 min-h-5 break-words text-[14px] leading-6 text-[#303a43] [overflow-wrap:anywhere] [&_.md-sub-text]:block [&_.md-sub-text]:overflow-visible [&_.md-sub-text]:text-clip [&_.md-sub-text]:whitespace-normal [&_.md-sub-text]:text-[14px] [&_.md-sub-text]:leading-6 [&_.md-sub-text]:text-[#303a43] [&_.md-sub-text_a]:text-[#245f91] [&_.md-sub-text_img]:max-w-full">
              {controls.content.text}
            </div>

            {display.permissions.canViewContent && !display.access.isContentLocked && display.media.attachments.length > 0 ? (
              <div className="md-legacy-answer-attachments mt-3 grid min-w-0 grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2 max-[640px]:grid-cols-1">
                {display.media.attachments.map((attachment, index) => {
                  const label = attachmentLabel(attachment)
                  const isImage = Boolean(attachment.thumbSrc && (attachment.width || attachment.height))
                  const content = (
                    <>
                      <span className="flex h-11 w-11 flex-none items-center justify-center overflow-hidden rounded border border-[#d8e0e7] bg-[#edf2f6] text-[10px] font-bold text-[#526574]">
                        {isImage ? (
                          <span className="relative flex h-full w-full items-center justify-center">
                            <span>{attachmentExtension(attachment)}</span>
                            <img
                              className="absolute inset-0 h-full w-full object-cover"
                              src={attachment.thumbSrc}
                              alt=""
                              onError={(event) => { event.currentTarget.style.display = 'none' }}
                            />
                          </span>
                        ) : attachmentExtension(attachment)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-medium text-[#30485b]">{label}</span>
                        <span className="mt-0.5 block text-[11px] text-[#7a8791]">{attachmentSize(attachment.size) || 'Attachment'}</span>
                      </span>
                    </>
                  )

                  return attachment.src ? (
                    <a
                      key={`${attachment.id}-${index}`}
                      className="flex min-w-0 items-center gap-2 rounded-md border border-[#dce3e9] bg-[#fafbfc] p-2 no-underline hover:border-[#aebdca] hover:bg-[#f5f8fa]"
                      href={attachment.src}
                      title={label}
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={`${attachment.id}-${index}`} className="flex min-w-0 items-center gap-2 rounded-md border border-[#dce3e9] bg-[#fafbfc] p-2">
                      {content}
                    </div>
                  )
                })}
              </div>
            ) : null}

            <footer className="md-legacy-answer-actions mt-3 flex min-h-5 flex-wrap items-center gap-x-3 gap-y-2 border-t border-[#edf0f2] pt-2.5 text-[12px] text-[#5e6b75] [&_.md-sub-icon]:m-0 [&_.md-sub-meta]:flex [&_.md-sub-meta]:flex-wrap [&_.md-sub-meta-link]:m-0 [&_.md-sub-meta-link]:text-[12px] [&_.md-sub-meta-link]:text-[#315f7d] [&_.md-sub-meta-more]:m-0 [&_.md-sub-meta-more]:ml-2 [&_.md-sub-meta-more]:text-[12px] [&_.md-sub-meta-more]:text-[#315f7d]">
              {display.permissions.canReply ? (
                <span className="relative inline-flex h-7 w-[68px] items-center justify-center text-[12px] font-medium text-[#315f7d] [&_.md-sub-icon]:absolute [&_.md-sub-icon]:inset-0 [&_.md-sub-icon]:h-full [&_.md-sub-icon]:w-full [&_.md-sub-icon]:rounded-md [&_.md-sub-icon]:border-[#c9d5df] [&_.md-sub-icon]:bg-[#f7f9fb] hover:[&_.md-sub-icon]:border-[#9fb2c1] hover:[&_.md-sub-icon]:bg-[#edf3f7]">
                  {controls.actions.quickReply}
                  <span className="pointer-events-none relative">Reply</span>
                </span>
              ) : null}
              <span className={`relative inline-flex h-7 w-[76px] items-center justify-center text-[12px] font-medium [&_.md-sub-icon]:absolute [&_.md-sub-icon]:inset-0 [&_.md-sub-icon]:h-full [&_.md-sub-icon]:w-full [&_.md-sub-icon]:rounded-md${display.isFavorite ? ' text-white [&_.md-sub-icon]:border-[#315f7d] [&_.md-sub-icon]:bg-[#315f7d]' : ' text-[#315f7d] [&_.md-sub-icon]:border-[#c9d5df] [&_.md-sub-icon]:bg-[#f7f9fb] hover:[&_.md-sub-icon]:border-[#9fb2c1] hover:[&_.md-sub-icon]:bg-[#edf3f7]'}`}>
                {controls.actions.favorite}
                <span className="pointer-events-none relative">{display.isFavorite ? 'Saved' : 'Favorite'}</span>
              </span>
              {controls.groups.meta}
            </footer>
          </div>
        </div>
      </article>

      <div className={`md-legacy-answer-nested clear-both ml-7 border-l border-[#d8e0e7] pl-3 max-[640px]:ml-3 max-[640px]:pl-2${hasChildren ? '' : ' hidden'}`}>
        {slots.allChildren}
      </div>
    </>
  )
}
